import { NOTIFICATION_ACTION_TYPES } from "./notificationActions";

// Strip non-serializable fields (e.g. React Router history) so Redux state stays serializable
const sanitizeNotification = (notification) => {
    if (!notification || typeof notification !== "object") return notification;
    const { history: _history, ...rest } = notification;
    return rest;
};

const initialState = {
    notifications: [],
    unreadCount: 0,
    loading: false,
    error: null,
    unreadCountsLoading: false,
    unreadCountsError: null
};

const notificationReducer = (state = initialState, action) => {
    switch (action.type) {
        case NOTIFICATION_ACTION_TYPES.GET_USER_NOTIFICATIONS_REQUEST:
            return {
                ...state,
                loading: true,
                error: null
            };

        case NOTIFICATION_ACTION_TYPES.GET_USER_NOTIFICATIONS_SUCCESS:
            // Handle both old format (array) and new format (object with notifications and flags)
            const payload = action.payload || {};
            const rawNotifications = Array.isArray(payload) ? payload : (payload.notifications || []);
            const newNotifications = rawNotifications.map(sanitizeNotification);
            const isRefresh = Array.isArray(payload) ? false : (payload.isRefresh === true);
            const isPreview = Array.isArray(payload) ? false : (payload.isPreview === true);

            // Don't calculate unread count from notifications array
            // The unread count should come from GetUnSeenCounts API only

            let updatedNotifications;

            // If this is a preview request (from GeneralNotification), only replace if we have fewer notifications
            if (isPreview) {
                // For preview: only replace if current list is smaller than what we're loading
                // This prevents GeneralNotification from replacing NotificationsScreen's larger list
                if (state.notifications.length < newNotifications.length) {
                    updatedNotifications = newNotifications;
                } else {
                    // Keep existing notifications if we already have more
                    updatedNotifications = state.notifications;
                }
            }
            // If isRefresh flag is explicitly set to true, always replace
            else if (isRefresh) {
                updatedNotifications = newNotifications;
            } else {
                // Check if this is a "load more" operation
                const hasExistingNotifications = state.notifications.length > 0;
                const hasNewNotifications = newNotifications.length > 0;

                // Check for overlap - if any new notification ID exists in current list, it's a refresh
                const hasOverlap = hasExistingNotifications && hasNewNotifications &&
                    state.notifications.some(existing =>
                        newNotifications.some(newNotif => newNotif.id === existing.id)
                    );

                // If there's overlap, it's a refresh (replace)
                // If no overlap and we have existing notifications, it's likely load more
                const isLoadMore = hasExistingNotifications &&
                    hasNewNotifications &&
                    !hasOverlap &&
                    newNotifications[0].id !== state.notifications[0].id;

                if (isLoadMore) {
                    // Append new notifications, avoiding duplicates
                    const existingIds = new Set(state.notifications.map(n => n.id));
                    const uniqueNewNotifications = newNotifications.filter(n => !existingIds.has(n.id));
                    updatedNotifications = [...state.notifications, ...uniqueNewNotifications];
                } else {
                    // Replace notifications (initial load or refresh)
                    updatedNotifications = newNotifications;
                }
            }

            return {
                ...state,
                notifications: updatedNotifications,
                loading: false,
                error: null
            };

        case NOTIFICATION_ACTION_TYPES.GET_USER_NOTIFICATIONS_FAILURE:
            return {
                ...state,
                loading: false,
                error: action.payload
            };

        case NOTIFICATION_ACTION_TYPES.GET_UNSEEN_COUNTS_REQUEST:
            return {
                ...state,
                unreadCountsLoading: true,
                unreadCountsError: null
            };

        case NOTIFICATION_ACTION_TYPES.GET_UNSEEN_COUNTS_SUCCESS:
            return {
                ...state,
                unreadCount: action.payload || 0,
                unreadCountsLoading: false,
                unreadCountsError: null
            };

        case NOTIFICATION_ACTION_TYPES.GET_UNSEEN_COUNTS_FAILURE:
            return {
                ...state,
                unreadCountsLoading: false,
                unreadCountsError: action.payload
            };


        case NOTIFICATION_ACTION_TYPES.MARK_ALL_NOTIFICATIONS_SEEN_REQUEST:
            return {
                ...state,
                loading: true
            };

        case NOTIFICATION_ACTION_TYPES.MARK_ALL_NOTIFICATIONS_SEEN_SUCCESS:
            const allSeenNotifications = state.notifications.map(notification => ({
                ...notification,
                isSeen: true
            }));

            return {
                ...state,
                notifications: allSeenNotifications,
                unreadCount: 0,
                loading: false
            };

        case NOTIFICATION_ACTION_TYPES.MARK_ALL_NOTIFICATIONS_SEEN_FAILURE:
            return {
                ...state,
                loading: false,
                error: action.payload
            };

        case NOTIFICATION_ACTION_TYPES.ADD_NOTIFICATION:
            const newNotification = sanitizeNotification(action.payload);
            const existingNotifications = state.notifications.filter(n => n.id !== newNotification.id);
            const updatedNotificationsWithNew = [newNotification, ...existingNotifications];

            return {
                ...state,
                notifications: updatedNotificationsWithNew,
                // Don't calculate unread count here - it should come from GetUnSeenCounts API
            };

        case NOTIFICATION_ACTION_TYPES.UPDATE_NOTIFICATION:
            const updatedNotification = sanitizeNotification(action.payload);
            const notificationsWithUpdate = state.notifications.map(notification =>
                notification.id === updatedNotification.id ? updatedNotification : notification
            );

            return {
                ...state,
                notifications: notificationsWithUpdate,
                // Don't calculate unread count here - it should come from GetUnSeenCounts API
            };

        case NOTIFICATION_ACTION_TYPES.DELETE_NOTIFICATION:
            const notificationId = action.payload;
            const notificationsWithoutDeleted = state.notifications.filter(notification => notification.id !== notificationId);

            return {
                ...state,
                notifications: notificationsWithoutDeleted,
                // Don't calculate unread count here - it should come from GetUnSeenCounts API
            };

        case NOTIFICATION_ACTION_TYPES.INCREMENT_UNREAD_COUNT:
            return {
                ...state,
                unreadCount: state.unreadCount + 1
            };

        case NOTIFICATION_ACTION_TYPES.CLEAR_NOTIFICATIONS:
            return {
                ...state,
                notifications: [],
                unreadCount: 0,
                error: null
            };

        default:
            return state;
    }
};

export default notificationReducer;
